import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Sparkles, Loader2, Info, CheckCircle2 } from 'lucide-react';
import api from '../../services/api';

/**
 * AI Max do Google (só campanhas de Pesquisa): amplia as buscas atendidas,
 * personaliza textos e escolhe a URL final por IA. Lê o estado ao vivo no
 * Google e, ao mudar, o backend relê para confirmar.
 */
const GoogleAiMaxToggle: React.FC<{ campaignId: string; compact?: boolean }> = ({ campaignId, compact }) => {
  const { t } = useTranslation();
  const [state, setState] = useState<{ supported: boolean; enabled: boolean; pending?: boolean; channel?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setState((await api.get(`/campaigns/${campaignId}/google-ai-max`)).data.data); }
    catch (e: any) { setError(e?.response?.data?.message || 'Erro'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campaignId]);

  const toggle = async () => {
    if (!state) return;
    const next = !state.enabled;
    if (next && !window.confirm(t('campaigns.aiMax.confirm', 'Ligar o AI Max? O Google passa a mostrar o anúncio em buscas mais amplas, reescrever textos e escolher a página de destino. Pode aumentar o alcance e também o gasto em buscas menos exatas.') as string)) return;
    if (!next && !window.confirm(t('campaigns.aiMax.confirmOff', 'Desligar o AI Max? A personalização de texto e a expansão de URL desta campanha também serão desligadas, e o Google volta a atender só as suas palavras-chave.') as string)) return;
    setSaving(true); setError(null);
    try {
      const r = await api.post(`/campaigns/${campaignId}/google-ai-max`, { enabled: next });
      setState(r.data.data);
      toast.success(r.data.message || (next ? 'AI Max ligado' : 'AI Max desligado'));
      if (r.data.data && r.data.data.warning) toast(r.data.data.warning, { icon: 'ℹ️' });
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Erro';
      setError(msg); toast.error(msg);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />{t('campaigns.aiMax.loading', 'Conferindo o AI Max no Google...')}</div>;
  if (state && !state.supported) {
    return compact ? null : <p className="text-xs text-gray-500 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" />{t('campaigns.aiMax.onlySearch', 'AI Max só existe em campanhas de Pesquisa do Google.')}</p>;
  }
  return (
    <div className={`rounded-xl border ${state?.enabled ? 'border-violet-300 bg-violet-50/60 dark:border-violet-800 dark:bg-violet-900/20' : 'border-gray-200 dark:border-gray-700'} p-4`}>
      <div className="flex items-start gap-3">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${state?.enabled ? 'bg-violet-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}><Sparkles className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">{t('campaigns.aiMax.title', 'AI Max para Pesquisa')}</span>
            {state?.enabled && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 dark:text-violet-300"><CheckCircle2 className="w-3.5 h-3.5" />{state.pending ? t('campaigns.aiMax.onPublish', 'liga ao publicar') : t('campaigns.aiMax.on', 'ligado no Google')}</span>}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('campaigns.aiMax.desc', 'A IA do Google amplia as buscas atendidas, personaliza os textos e escolhe a melhor página de destino. Bom para escalar campanhas que já convertem.')}</p>
          {error && <p className="text-xs text-red-600 dark:text-red-400 mt-1.5">{error}</p>}
        </div>
        <button type="button" role="switch" aria-checked={!!state?.enabled} aria-label={t('campaigns.aiMax.title', 'AI Max para Pesquisa') as string} onClick={toggle} disabled={saving}
          className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-60 ${state?.enabled ? 'bg-violet-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin text-white m-1" /> : <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform ${state?.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />}
        </button>
      </div>
    </div>
  );
};

export default GoogleAiMaxToggle;
